import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HotelIcon from "@mui/icons-material/Hotel";

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 0",
  cursor: "pointer",
  userSelect: "none",
});

const ReferralCard = styled(Paper)(({ isselected }) => ({
  padding: 16,
  cursor: "pointer",
  border: isselected === "true" ? "2px solid #1976d2" : "1px solid #21cdc0",
  backgroundColor: isselected === "true" ? "#d0f5f3" : "#EBFAF9",
  transition: "all 0.2s ease",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0px 4px 15px rgba(0,0,0,0.12)",
  },
}));

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const REFERRAL_IN_IPD_ROLE = "referral_in_ipd";

const getCurrentDoctorId = () => sessionStorage.getItem("doctor_id") || "";

const isReferralInIpdRole = (role) => role === REFERRAL_IN_IPD_ROLE;

// Current user's active referral-in-IPD link for this admission
const getMyReferralInIpdLink = (referral) => {
  const doctorId = getCurrentDoctorId();
  const attending = referral.doctors?.attending || [];
  if (!doctorId) {
    return attending.find((a) => isReferralInIpdRole(a.role)) || null;
  }
  return (
    attending.find(
      (a) => isReferralInIpdRole(a.role) && String(a.id) === String(doctorId)
    ) || null
  );
};

const shouldShowInReferredToMe = (referral) => Boolean(getMyReferralInIpdLink(referral));

const filterReferredToMePatients = (list) =>
  (Array.isArray(list) ? list : []).filter(shouldShowInReferredToMe);

const isReferralLinkAccepted = (link) => {
  const status = (link?.status || "").toLowerCase();
  return status === "accepted" || status === "admitted";
};

const isReferralAccepted = (referral, localAccepted = {}) => {
  const admissionId = referral.admission_id;
  if (localAccepted[admissionId]) return true;
  return isReferralLinkAccepted(getMyReferralInIpdLink(referral));
};

const isReferralPending = (referral, localAccepted = {}) => {
  const link = getMyReferralInIpdLink(referral);
  if (!link) return false;
  if (isReferralAccepted(referral, localAccepted)) return false;
  const status = (link.status || "").toLowerCase();
  return status === "pending" || status === "";
};

// [BILLING] billing_provider_id for current doctor from referred_to_me ipd_admission response.
const getMyReferralBillingProviderId = (referral) => {
  const doctorId = getCurrentDoctorId();
  if (!doctorId) return "";

  const referringDoctor = referral?.referring_doctor;
  if (
    referringDoctor?.billing_provider_id &&
    String(referringDoctor.doctor_id || referringDoctor.id) === String(doctorId)
  ) {
    return String(referringDoctor.billing_provider_id);
  }

  const myLink = getMyReferralInIpdLink(referral);
  if (myLink?.billing_provider_id) {
    return String(myLink.billing_provider_id);
  }

  const attendingMatch = (referral?.doctors?.attending || []).find(
    (doc) =>
      String(doc.id || doc.doctor_id) === String(doctorId) && doc.billing_provider_id
  );
  return attendingMatch?.billing_provider_id
    ? String(attendingMatch.billing_provider_id)
    : "";
};

// Build a patient object compatible with PatientDetailsPanel from referral data
const normalizeReferralToPatient = (referral) => {
  const dob = referral.patient?.dob;
  let age = null;
  if (dob) {
    const year = Number(String(dob).split("-")[0]);
    if (Number.isFinite(year)) age = new Date().getFullYear() - year;
  }

  return {
    id: String(referral.admission_id),
    admissionId: referral.admission_id,
    pat_id: referral.patient?.patient_id || "",
    patient_id: referral.patient?.patient_id || "",
    patientId: referral.patient?.patient_id || "",
    name: referral.patient?.name || "Unknown Patient",
    // The shared PatientDetailsPanel / OrderManagementDrawer render the ward,
    // which is the bed type. Without this a referred-in patient shows "Ward: —".
    bed_type_name: referral.bed_type_name || "",
    wardNo: "",
    roomNo: "",
    bedNo: referral.bed_id || "",
    type: "Referral",
    condition: getMyReferralInIpdLink(referral)?.notes || "",
    diseases: [],
    age,
    referralAccepted: false,
    gender:
      referral.patient?.gender === "M"
        ? "Male"
        : referral.patient?.gender === "F"
          ? "Female"
          : referral.patient?.gender || null,
    admissionDate: referral.admitted_at?.split?.("T")?.[0] || "",
    vitals: {
      bloodPressure: "--/--",
      heartRate: "--",
      temperature: "--",
      oxygenSaturation: "--",
      respiratoryRate: "--",
      weight: "--",
      lastUpdated: new Date().toLocaleString(),
    },
    carePlans: [],
    tasks: [],
    // [LAB_TEST] From ipd_admission admission_details — passed to CarePlanActivityModal on Test save.
    careContextRef: referral.admission_details?.care_context_ref || "",
    doctorName: referral.doctor_name || referral.doctors?.admitting?.name || "",
    // [BILLING] For billing/charges/ when patient is from Referred To Me section.
    referredByProviderId: getMyReferralBillingProviderId(referral),
    referredDoctors: (referral.doctors?.attending || [])
      .filter((doc) => isReferralInIpdRole(doc.role))
      .map((doc) => ({
        id: doc.id || doc.doctor_id,
        name: doc.name || "",
        specialty: doc.specialty || "",
        initials: getInitials(doc.name),
        isPrimary: doc.role === "primary",
        role: doc.role,
      })),
  };
};

// Status badge style helper
const getStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "pending")
    return { bgcolor: "#fff3e0", color: "#e65100", border: "1px solid #e65100" };
  if (s === "accepted" || s === "admitted")
    return { bgcolor: "#e8f5e9", color: "#2e7d32", border: "1px solid #2e7d32" };
  if (s === "declined")
    return { bgcolor: "#fce4ec", color: "#c62828", border: "1px solid #c62828" };
  return { bgcolor: "#e3f2fd", color: "#1565c0", border: "1px solid #1565c0" };
};

const ReferredToMe = ({ patients, selectedPatientId, onSelectPatient, onCountChange }) => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalText, setNotesModalText] = useState("");
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declineTarget, setDeclineTarget] = useState(null);

  // Same base URL as IpdDataContext
  const BASE =
    process.env.REACT_APP_BACKEND_ROUTE_URL || "http://localhost.dev.com:8000/app/api/";

  const getHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
  });

  const referredToMeUrl = `${BASE}ipd_admission/referred_to_me/?status=Allocated&since=2026-01-01`;

  const fetchReferrals = async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const res = await axios.get(referredToMeUrl, { headers: getHeaders() });
      const rawList = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      const list = filterReferredToMePatients(rawList);
      setReferrals(list);
      if (onCountChange) onCountChange(list.length);
      return list;
    } catch (err) {
      console.error("[ReferredToMe] Error:", err?.response?.status, err?.message);
      setError("Failed to load referrals");
      return null;
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals({ showLoading: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local accepted tracks same-session accept; API link.status is source of truth on load.
  const [localAccepted, setLocalAccepted] = useState({});

  const pendingCount = referrals.filter((r) =>
    isReferralPending(r, localAccepted)
  ).length;

  const handleOpenDeclineModal = (admissionId, referralDoctorId, patientName, e) => {
    e.stopPropagation();
    setDeclineTarget({ admissionId, referralDoctorId, patientName });
    setDeclineReason("");
    setDeclineModalOpen(true);
  };

  const handleCloseDeclineModal = () => {
    setDeclineModalOpen(false);
    setDeclineReason("");
    setDeclineTarget(null);
  };

  const handleConfirmDecline = async () => {
    if (!declineTarget) return;
    const reason = declineReason.trim();
    if (!reason) return;

    const { admissionId, referralDoctorId } = declineTarget;
    setActionLoading((prev) => ({ ...prev, [admissionId]: "Declined" }));
    try {
      await axios.post(
        `${BASE}ipd_admission/${admissionId}/doctors/${referralDoctorId}/respond/`,
        { action: "reject", reason },
        { headers: getHeaders() }
      );
      setReferrals((prev) =>
        prev.filter((r) => String(r.admission_id) !== String(admissionId))
      );
      if (String(selectedPatientId) === String(admissionId)) {
        onSelectPatient(null);
      }
      handleCloseDeclineModal();
    } catch (err) {
      console.error("[ReferredToMe] Decline error:", err?.response?.data || err?.message);
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[admissionId];
        return next;
      });
    }
  };

  const handleAction = async (admissionId, newStatus, referralDoctorId, e) => {
    e.stopPropagation();
    setActionLoading((prev) => ({ ...prev, [admissionId]: newStatus }));
    try {
      if (newStatus === "Accepted") {
        // [OLD CODE]
        // await axios.post(
        //   `${BASE}ipd_admission/${admissionId}/doctors/`,
        //   { doctor_id: referralDoctorId, role: "consultant" },
        //   { headers: getHeaders() }
        // );
        // [NEW CODE - Referred to Me feature]
        await axios.post(
          `${BASE}ipd_admission/${admissionId}/doctors/${referralDoctorId}/respond/`,
          { action: "accept" },
          { headers: getHeaders() }
        );
        setLocalAccepted((prev) => ({ ...prev, [admissionId]: true }));
        setReferrals((prev) =>
          prev.map((r) => {
            if (String(r.admission_id) !== String(admissionId)) return r;
            return {
              ...r,
              doctors: {
                ...r.doctors,
                attending: (r.doctors?.attending || []).map((a) =>
                  String(a.id) === String(referralDoctorId) &&
                    (a.role === "referred" || isReferralInIpdRole(a.role))
                    ? { ...a, status: "accepted", role: REFERRAL_IN_IPD_ROLE }
                    : a
                ),
              },
            };
          })
        );
        const acceptedReferral = referrals.find(
          (r) => String(r.admission_id) === String(admissionId)
        );
        if (acceptedReferral) {
          const existing = patients.find(
            (p) =>
              String(p.admissionId) === String(admissionId) ||
              String(p.id) === String(admissionId)
          );
          onSelectPatient(
            existing || {
              ...normalizeReferralToPatient(acceptedReferral),
              referralAccepted: true,
            }
          );
        }
      }
    } catch (err) {
      console.error("[ReferredToMe] Action error:", err?.response?.data || err?.message);
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[admissionId];
        return next;
      });
    }
  };

  const handleCloseInvolvement = async (admissionId, e) => {
    e.stopPropagation();
    setActionLoading((prev) => ({ ...prev, [admissionId]: "Close" }));
    try {
      const res = await axios.post(
        `${BASE}ipd_admission/${admissionId}/close/`,
        {},
        { headers: getHeaders() }
      );
      if (res.data?.status === "Success") {
        setLocalAccepted((prev) => {
          const next = { ...prev };
          delete next[admissionId];
          return next;
        });
        if (String(selectedPatientId) === String(admissionId)) {
          onSelectPatient(null);
        }
        await fetchReferrals();
      }
    } catch (err) {
      console.error(
        "[ReferredToMe] Close involvement error:",
        err?.response?.data || err?.message
      );
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[admissionId];
        return next;
      });
    }
  };

  const handleCardClick = (referral) => {
    const admissionId = referral.admission_id;
    if (!isReferralAccepted(referral, localAccepted)) {
      return;
    }
    const existing = patients.find(
      (p) =>
        String(p.admissionId) === String(admissionId) ||
        String(p.id) === String(admissionId)
    );
    onSelectPatient(
      existing || {
        ...normalizeReferralToPatient(referral),
        referralAccepted: true,
      }
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      <SectionHeader onClick={() => setExpanded((v) => !v)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ArrowForwardIcon sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600} color="#121959">
            Referred To Me
          </Typography>
          <Chip
            label={referrals.length}
            size="small"
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              height: 20,
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          />
        </Box>
        {pendingCount > 0 && (
          <Chip
            label={`${pendingCount} pending`}
            size="small"
            sx={{ bgcolor: "#ff9800", color: "#fff", fontSize: "0.75rem", fontWeight: 600 }}
          />
        )}
      </SectionHeader>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : referrals.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary" variant="body2">
              No referrals at this time
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {referrals.map((referral) => {
              const admissionId = referral.admission_id;
              const referralLink = getMyReferralInIpdLink(referral);
              const referralDoctorId = referralLink?.id;
              const isAccepted = isReferralAccepted(referral, localAccepted);
              const isPending = isReferralPending(referral, localAccepted);
              const linkStatus = (referralLink?.status || "").toLowerCase();
              const referralStatus = isAccepted
                ? "Accepted"
                : linkStatus
                  ? linkStatus.charAt(0).toUpperCase() + linkStatus.slice(1)
                  : "Pending";

              const referralNotes = (referralLink?.notes || "").trim();
              const referringDoctor = referral.doctors?.admitting?.name || "";
              const referringSpecialty = referral.doctors?.admitting?.specialty || "";
              const referralDate = referral.admitted_at?.split?.("T")?.[0] || "--";

              const isSelected =
                String(selectedPatientId) === String(admissionId) ||
                selectedPatientId === `ref-${admissionId}`;

              return (
                <Grid item xs={12} sm={6} md={4} key={admissionId} sx={{ display: "flex" }}>
                  <ReferralCard
                    elevation={1}
                    isselected={isSelected ? "true" : "false"}
                    onClick={() => handleCardClick(referral)}
                  >
                    {/* Header: Name + Status badge */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ color: "#121959", flex: 1, pr: 1 }}
                      >
                        {referral.patient?.name || "Unknown Patient"}
                      </Typography>
                      <Chip
                        label={referralStatus}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          flexShrink: 0,
                          ...getStatusStyle(referralStatus),
                        }}
                      />
                    </Box>

                    {/* Bed info (API only returns bed_id, no separate ward/room) */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                      <HotelIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Bed: {referral.bed_id || "--"} · Patient ID: {referral.patient?.patient_id || "--"}
                      </Typography>
                    </Box>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Referring doctor */}
                    {referringDoctor && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                          {referringDoctor}
                          {referringSpecialty && ` (${referringSpecialty})`}
                        </Typography>
                      </Box>
                    )}

                    {/* Referred / admitted date */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Referred: {referralDate}
                      </Typography>
                    </Box>

                    {/* {referralNotes && ( */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      onClick={
                        referralNotes?.length > 0
                          ? (e) => {
                            e.stopPropagation();
                            setNotesModalText(referralNotes);
                            setNotesModalOpen(true);
                          }
                          : undefined
                      }
                      sx={{
                        display: "block",
                        mt: 0.5,
                        mb: isPending || isAccepted ? 1 : 0,
                        cursor: "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "0.75rem",
                        color: "#444",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Notes: {referralNotes || "--"}
                    </Typography>
                    {/* )} */}

                    {/* {!referralNotes && (
                      <Box sx={{ mb: isPending ? 1.5 : 0 }} />
                    )} */}

                    {/* Accept / Decline buttons for pending referrals */}
                    {isPending && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!!actionLoading[admissionId]}
                          onClick={(e) => handleAction(admissionId, "Accepted", referralDoctorId, e)}
                          sx={{
                            flex: 1,
                            bgcolor: "#00897b",
                            "&:hover": { bgcolor: "#00695c" },
                            textTransform: "none",
                            fontWeight: 600,
                            minHeight: 32,
                          }}
                        >
                          {actionLoading[admissionId] === "Accepted" ? (
                            <CircularProgress size={14} sx={{ color: "#fff" }} />
                          ) : (
                            "Accept"
                          )}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!!actionLoading[admissionId]}
                          onClick={(e) =>
                            handleOpenDeclineModal(
                              admissionId,
                              referralDoctorId,
                              referral.patient?.name || "Unknown Patient",
                              e
                            )
                          }
                          sx={{
                            flex: 1,
                            borderColor: "#9e9e9e",
                            color: "#616161",
                            textTransform: "none",
                            fontWeight: 600,
                            minHeight: 32,
                            "&:hover": { borderColor: "#424242", color: "#424242" },
                          }}
                        >
                          {actionLoading[admissionId] === "Declined" ? (
                            <CircularProgress size={14} />
                          ) : (
                            "Decline"
                          )}
                        </Button>
                      </Box>
                    )}

                    {isAccepted && (
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        disabled={!!actionLoading[admissionId]}
                        onClick={(e) => handleCloseInvolvement(admissionId, e)}
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          fontWeight: 600,
                          minHeight: 32,
                          borderColor: "#2e7d32",
                          color: "#2e7d32",
                          "&:hover": {
                            borderColor: "#1b5e20",
                            backgroundColor: "#e8f5e9",
                          },
                        }}
                      >
                        {actionLoading[admissionId] === "Close" ? (
                          <CircularProgress size={14} sx={{ color: "#2e7d32" }} />
                        ) : (
                          "Close Involvement"
                        )}
                      </Button>
                    )}
                  </ReferralCard>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Collapse>

      <Dialog
        open={declineModalOpen}
        onClose={handleCloseDeclineModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
            fontWeight: 600,
          }}
        >
          Decline Referral
          <IconButton
            aria-label="Close decline dialog"
            onClick={handleCloseDeclineModal}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for declining
            {declineTarget?.patientName ? ` ${declineTarget.patientName}'s` : " this"} referral.
          </Typography>
          <TextField
            fullWidth
            label="Reason for declining"
            placeholder="e.g. Not available this week; please assign another consultant"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            multiline
            minRows={3}
            maxRows={8}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseDeclineModal}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={
              !declineReason.trim() ||
              (declineTarget &&
                actionLoading[declineTarget.admissionId] === "Declined")
            }
            onClick={handleConfirmDecline}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {declineTarget && actionLoading[declineTarget.admissionId] === "Declined" ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Confirm Decline"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
            fontWeight: 600,
          }}
        >
          Notes
          <IconButton
            aria-label="Close notes"
            onClick={() => setNotesModalOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
            {notesModalText}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReferredToMe;
